import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Warning, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DateField } from '@/components/ui/date-time-field';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import { LocationPicker } from '@/components/registration';
import { MaterialLines, isResolved } from './development/MaterialLines';
import { devApi } from './development/devApi';
import { EngAssetFormDialog } from './EngAssetFormDialog';
import { engApi } from './engApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FAB = `${API}/engineering/fabrication`;

export const STATUS_FLOW = [
  { value: 'todo', label: '未着手' },
  { value: 'in_progress', label: '製作中' },
  { value: 'done', label: '完成' },
  { value: 'delivered', label: '納品済' },
  { value: 'cancelled', label: '取消' },
];
const STATUS_COLOR = {
  todo: 'bg-zinc-100 text-zinc-700', in_progress: 'bg-sky-100 text-sky-800',
  done: 'bg-teal-100 text-teal-800', delivered: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-zinc-100 text-zinc-400 line-through',
};

const EMPTY = { name: '', part_no: '', item_id: '', item_source: '', qty: 1, uom: '個',
  make_method: '', needed_by: '', assignee_id: '', assignee_name: '',
  fab_data_path: '', notes: '', origin_type: 'none', origin_id: '', origin_label: '',
  material_lines: [],
  // 納入先 — どこへ渡すか。完成したときの入庫先もここになる。
  deliver_register: '', deliver_location_id: '', deliver_location: '',
  direct_use: false };

// 製作依頼の品目は「作る物」。材料ではないので種別で絞らない（原材料も半製品も作る）。
// 備品・IT の品目も作れる（オーナー決定 2026-06）。設備そのものは作らないので外す。
const ITEM_SOURCES = ['mfg', 'eng', 'it', 'office'];
// マスター検索の台帳名 → 製作依頼が持つ台帳名
const SOURCE_TO_REGISTER = { mfg: 'mfg', eng: 'eng', equip: 'eng', it: 'it', office: 'bihin' };
const REGISTERS = [
  { value: 'eng', label: '技術' }, { value: 'mfg', label: '製造' },
  { value: 'it', label: 'IT' }, { value: 'bihin', label: '備品' },
];

/**
 * 製作依頼（自社製作）— 技術が自分で作る部品の一覧。
 *
 * 購入品は 部品依頼（REQ）→ TECHS のまま。ここは **TECHS に出さない** 仕事だけ。
 * 完成にすると 使用材料 × 数量 が 1 回だけ出庫される（戻すと打ち消す）。
 */
export default function EngFabrication() {
  const [rows, setRows] = useState([]);
  const [methods, setMethods] = useState([]);
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState('all');
  const [assignee, setAssignee] = useState('');
  const [overdue, setOverdue] = useState(false);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  // 未登録の品目は、この場で技術資産として登録する（F1 と同じフォーム・同じ品番ルール）
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [defaultsFrom, setDefaultsFrom] = useState(null);
  const [params] = useSearchParams();

  const load = useCallback(() => {
    const params = { status, overdue: overdue || undefined, assignee_id: assignee || undefined, q: q || undefined };
    axios.get(FAB, { params }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [status, overdue, assignee, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${FAB}/methods`).then((r) => setMethods(r.data || [])).catch(() => setMethods([]));
    engApi.members?.().then((d) => setUsers(d?.users || [])).catch(() => setUsers([]));
  }, []);

  const counts = useMemo(() => ({
    open: rows.filter((r) => ['todo', 'in_progress'].includes(r.status)).length,
    overdue: rows.filter((r) => r.overdue).length,
  }), [rows]);

  // 品目を選んだら「作り方」も連れてくる。製作方法・使用材料・製作データは
  // 同じ品目の直近の製作依頼から入る（前回と同じ作り方で始められる）。
  const applyItem = useCallback(async (picked) => {
    const src = SOURCE_TO_REGISTER[picked.source] || 'mfg';
    setForm((f) => ({
      ...f, item_id: picked.id, item_source: src,
      name: picked.name || f.name, part_no: picked.sku || '',
      uom: picked.uom || f.uom || '個',
    }));
    setDefaultsFrom(null);
    try {
      const { data } = await axios.get(`${FAB}/item-defaults`,
        { params: { item_id: picked.id, item_source: src } });
      setForm((f) => ({
        ...f,
        uom: data.uom || f.uom,
        // 納入先の初期値: その品目が今いる台帳と保管場所（変えられる）
        deliver_register: f.deliver_register || data.default_register || src,
        deliver_location_id: f.deliver_location_id || data.default_location_id || '',
        deliver_location: f.deliver_location || data.default_location_name || '',
        make_method: data.make_method || f.make_method,
        fab_data_path: data.fab_data_path || f.fab_data_path,
        material_lines: (data.material_lines || []).length
          ? data.material_lines.map((m) => ({
            component_item_id: m.component_item_id, component_source: m.component_source || 'mfg',
            component_name: m.component_name || '', component_sku: m.component_sku || '',
            qty: m.qty_per_unit, qty_basis: 'per_unit', uom: m.uom || '', notes: m.notes || '',
          }))
          : f.material_lines,
      }));
      setDefaultsFrom(data.from_fab_no || null);
    } catch (_e) { /* 初期値が無いだけ。空で続ける */ }
  }, []);

  // 設備の「交換部品を依頼」などから開かれたとき（?origin_type=equipment&origin_id=…）。
  // 設備の交換部品は棚に入れずそのまま付けるので「直接使用」を既定で入れる。
  const openNew = () => {
    const ot = params.get('origin_type') || 'none';
    setForm({
      ...EMPTY,
      origin_type: ot,
      origin_id: params.get('origin_id') || '',
      origin_label: params.get('origin_label') || '',
      direct_use: ot === 'equipment',
      deliver_register: ot === 'equipment' ? 'eng' : '',
    });
    setDefaultsFrom(null);
    setOpen(true);
  };

  const save = async () => {
    if (!form.item_id) { toast.error('「品目」をマスターから選んでください（未登録なら新規品目として登録）'); return; }
    if (!(Number(form.qty) > 0)) { toast.error('「数量」を入力してください'); return; }
    if (!form.deliver_register) { toast.error('「納入先」の部署を選んでください'); return; }
    setBusy(true);
    try {
      const qty = Number(form.qty) || 1;
      await axios.post(FAB, {
        ...form,
        qty,
        deliver_to: {
          register: form.deliver_register,
          location_id: form.deliver_location_id || null,
          location_name: form.deliver_location || null,
        },
        // 1個あたりの使用量に直して保存する（出庫は 使用量 × 数量）
        material_lines: form.material_lines.filter(isResolved).map((l) => ({
          component_item_id: l.component_item_id,
          component_source: l.component_source === 'eng' ? 'eng' : 'mfg',
          component_name: l.component_name, component_sku: l.component_sku || null,
          qty_per_unit: l.qty_basis === 'total' ? Number(l.qty) / qty : Number(l.qty),
          uom: l.uom, notes: (l.notes || '').trim() || null,
        })),
      });
      toast.success('製作依頼を作成しました');
      setOpen(false); setForm(EMPTY); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '作成に失敗しました');
    } finally { setBusy(false); }
  };

  const move = async (row, next) => {
    try {
      const r = await axios.post(`${FAB}/${row.id}/status`, { status: next });
      (r.data?.warnings || []).forEach((w) => toast.warning(w));
      if (next === 'done' && !(r.data?.warnings || []).length) toast.success('完成にしました（材料を出庫しました）');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '状態の変更に失敗しました');
    }
  };

  return (
    <div className="space-y-4" data-testid="eng-fabrication-page">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-zinc-900">製作依頼</h1>
          <p className="text-[12px] text-zinc-500">
            自社で作る部品（3Dプリント・レーザー加工など）。購入品の発注は 部品受注入力 のままです。
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-800"
            data-testid="fab-count-open">対応が必要 {counts.open}</span>
          {counts.overdue > 0 && (
            <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700"
              data-testid="fab-count-overdue">期限超過 {counts.overdue}</span>
          )}
          <Button onClick={openNew} className="bg-teal-700 hover:bg-teal-800"
            data-testid="fab-new-btn">
            <Plus size={14} className="mr-1" />製作依頼を作成
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-2 text-sm" data-testid="fab-filter-status">
          <option value="all">すべての状態</option>
          {STATUS_FLOW.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-2 text-sm" data-testid="fab-filter-assignee">
          <option value="">担当すべて</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-[12px] text-zinc-600">
          <input type="checkbox" checked={overdue} onChange={(e) => setOverdue(e.target.checked)}
            className="h-4 w-4 accent-rose-600" data-testid="fab-filter-overdue" />
          期限超過のみ
        </label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="依頼No・品名・依頼元で検索"
          className="h-9 w-64" data-testid="fab-search" />
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
        {/* 15列あるので潰さず横スクロールさせる（潰れると 状態 が1文字ずつ折り返す） */}
        <table className="w-full min-w-[1700px] text-sm">
          <thead className="bg-zinc-50 text-[11px] text-zinc-500">
            <tr>
              {['依頼No', '品番', '品名', '数量', '納入先', '入庫先', '製作方法', '使用材料', '依頼元', '依頼者', '担当', '希望納期', '状態', '製作データ', ''].map((h) => (
                <th key={h} className={`px-3 py-2 text-left font-semibold whitespace-nowrap${h === '状態' ? ' min-w-20' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-zinc-50/60" data-testid={`fab-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-[12px] text-zinc-700">{r.fab_no}</td>
                <td className="px-3 py-2 font-mono text-[12px] text-zinc-500">{r.part_no || '—'}</td>
                <td className="px-3 py-2 text-zinc-800">{r.name}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.qty}{r.uom}</td>
                <td className="px-3 py-2 text-[12px] text-zinc-600" data-testid={`fab-deliver-${r.id}`}>
                  {r.deliver_to_label}
                </td>
                <td className="px-3 py-2 text-[12px] text-zinc-600" data-testid={`fab-receipt-${r.id}`}>
                  {r.receipt_label}
                  {r.delivered_by_name && (
                    <span className="block text-[10px] text-emerald-700">
                      受領 {r.delivered_by_name}・{(r.delivered_at || '').slice(0, 10)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px] text-zinc-600">{r.make_method || '—'}</td>
                <td className="px-3 py-2 text-[12px] text-zinc-600">
                  {(r.material_lines || []).length === 0 ? '—' : (
                    <span>
                      {(r.material_lines || []).map((m) => m.component_name || m.component_sku).filter(Boolean).join('、')}
                      <span className="ml-1 text-zinc-400">
                        計 {r.material_total}{(r.material_lines[0] || {}).uom}
                      </span>
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px] text-zinc-600">
                  {r.origin_label ? `${r.origin_type_label}: ${r.origin_label}` : '—'}
                </td>
                <td className="px-3 py-2 text-[12px] text-zinc-600">{r.requested_by_name || '—'}</td>
                <td className="px-3 py-2 text-[12px] text-zinc-600">{r.assignee_name || '未割当'}</td>
                <td className="px-3 py-2 whitespace-nowrap text-[12px]">
                  <span className={r.overdue ? 'font-semibold text-rose-700' : 'text-zinc-600'}>
                    {r.needed_by || '—'}
                  </span>
                  {r.overdue && <Warning size={12} className="ml-1 inline text-rose-600" />}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[r.status]}`}
                    data-testid={`fab-status-${r.id}`}>{r.status_label}</span>
                </td>
                <td className="px-3 py-2 max-w-48 truncate font-mono text-[11px] text-zinc-500" title={r.fab_data_path || ''}>
                  {r.fab_data_path || '—'}
                </td>
                <td className="px-3 py-2">
                  <select value={r.status} onChange={(e) => move(r, e.target.value)}
                    className="h-8 rounded-md border border-zinc-200 bg-white px-1.5 text-[12px]"
                    data-testid={`fab-status-select-${r.id}`}>
                    {STATUS_FLOW.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={15} className="px-3 py-10 text-center text-sm text-zinc-400"
                data-testid="fab-empty">製作依頼はありません</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="fab-form-dialog">
          <DialogHeader><DialogTitle>製作依頼を作成</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* 品目 — 他の台帳とまったく同じ共有ピッカー。品名・品番の手入力はしない。 */}
            <MasterItemPicker
              testIdPrefix="fab-form-item"
              label="品目（マスターから検索 または 新規入力）"
              placeholder="品名・品番・図番で検索（製造・技術資産・IT・備品）"
              helper="手入力はできません。候補から選ぶと 品名・品番・単位・製作方法・使用材料・製作データ が入ります。"
              search={devApi.masterSearch}
              sources={ITEM_SOURCES}
              onQueryChange={setTypedName}
              onPick={applyItem}
              linked={form.item_id ? { label: form.name, sku: form.part_no, sourceLabel: form.item_source === 'eng' ? '技術資産' : '製造' } : null}
              onUnlink={() => setForm((f) => ({ ...f, item_id: '', item_source: '', name: '', part_no: '' }))}
              unlinkLabel="選び直す"
              statusSlot={<span className="rounded bg-rose-100 px-1 text-[10px] font-bold text-rose-700" data-testid="fab-form-item-required">必須</span>}
            />
            {!form.item_id && (
              <Button type="button" variant="outline" className="h-9 w-full text-[12px]"
                onClick={() => { setOpen(false); setNewItemOpen(true); }} data-testid="fab-form-new-item">
                <Plus size={13} className="mr-1" />この品目はまだ無い — 新規品目として登録
              </Button>
            )}
            {defaultsFrom && (
              <p className="rounded bg-teal-50 px-2 py-1 text-[11px] text-teal-800" data-testid="fab-form-defaults-note">
                前回の製作依頼 {defaultsFrom} と同じ 製作方法・使用材料・製作データ を入れました。違っていれば直してください。
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">数量 <span className="text-rose-500">＊</span></Label>
                <Input type="number" min="1" value={form.qty}
                  onChange={(e) => setForm({ ...form, qty: e.target.value })}
                  className="h-10 mt-1 tabular-nums" data-testid="fab-form-qty" />
                <p className="mt-1 text-[11px] text-zinc-400">単位: {form.uom || '個'}（品目マスタの単位）</p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">希望納期</Label>
                <DateField value={form.needed_by} onChange={(v) => setForm({ ...form, needed_by: v })}
                  className="mt-1" inputClassName="h-10" testId="fab-form-needed-by" />
              </div>
            </div>

            {/* 納入先 — どこへ渡すか。完成したときの入庫先もここになる。 */}
            <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3 space-y-2" data-testid="fab-form-deliver">
              <p className="text-xs font-bold text-teal-800">納入先 <span className="text-rose-500">＊</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">部署（受け取る台帳）</Label>
                  <select value={form.deliver_register}
                    onChange={(e) => setForm({ ...form, deliver_register: e.target.value })}
                    className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                    data-testid="fab-form-deliver-register">
                    <option value="">選択してください</option>
                    {REGISTERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">場所</Label>
                  <div className="mt-1">
                    <LocationPicker value={form.deliver_location_id}
                      legacyText={form.deliver_location}
                      onChange={(id, row) => setForm((f) => ({
                        ...f, deliver_location_id: id || '',
                        deliver_location: (row && (row.path || row.name)) || '',
                      }))}
                      testId="fab-form-deliver-location" />
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-2 text-[12px] text-zinc-700">
                <input type="checkbox" checked={form.direct_use} className="mt-0.5"
                  onChange={(e) => setForm({ ...form, direct_use: e.target.checked })}
                  data-testid="fab-form-direct-use" />
                <span>
                  在庫に入れずに直接使用する
                  <span className="block text-[11px] text-zinc-500">
                    完成しても入庫しません。依頼元が設備のときは、その設備の保全記録に
                    「自社製作品を使用」として残します。
                  </span>
                </span>
              </label>
            </div>

            <div>
              <Label className="text-xs font-semibold text-zinc-600">製作方法</Label>
              <select value={form.make_method} onChange={(e) => setForm({ ...form, make_method: e.target.value })}
                className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                data-testid="fab-form-method">
                <option value="">（未選択）</option>
                {methods.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-zinc-400">候補は 設定 → マスター で編集できます</p>
            </div>

            {/* 使用材料 — 開発案件の部品行とまったく同じ部品。完成にすると
                使用量 × 数量 を 1 回だけ出庫する（手で作った依頼でも同じ）。 */}
            <MaterialLines
              lines={form.material_lines}
              onChange={(lines) => setForm((f) => ({ ...f, material_lines: lines }))}
            />

            <div>
              <Label className="text-xs font-semibold text-zinc-600">担当</Label>
              <select value={form.assignee_id}
                onChange={(e) => setForm({
                  ...form, assignee_id: e.target.value,
                  assignee_name: (users.find((u) => u.id === e.target.value) || {}).name || '',
                })}
                className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                data-testid="fab-form-assignee">
                <option value="">未割当</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">製作データ（NASの場所）</Label>
              <Input value={form.fab_data_path} onChange={(e) => setForm({ ...form, fab_data_path: e.target.value })}
                placeholder={'例: Z:\\技術部\\3Dデータ\\bracket.stl'}
                className="h-10 mt-1 font-mono text-[12px]" data-testid="fab-form-path" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">備考</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="h-10 mt-1" data-testid="fab-form-notes" />
            </div>
            <p className="rounded-lg bg-zinc-50 px-3 py-2 text-[11px] text-zinc-500" data-testid="fab-form-issue-note">
              完成にすると、使用材料 × 数量 を 1 回だけ出庫し、出来た物を
              {form.direct_use ? '（直接使用なので入庫はしません）'
                : `${(REGISTERS.find((r) => r.value === form.deliver_register) || {}).label || '納入先'}の在庫に 1 回だけ入庫`}
              します（完成から戻すと、どちらも打ち消します）。
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
            <Button onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="fab-form-save">
              {busy ? <ArrowsClockwise size={14} className="animate-spin" /> : null}
              <span className={busy ? 'ml-1' : ''}>作成</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 未登録の品目は F1（技術資産）と同じフォームで登録する。品番は 品番ルール
          から発番（手入力不可）。保存すると、そのまま品目として選ばれた状態で戻る。 */}
      <EngAssetFormDialog
        open={newItemOpen}
        onClose={() => { setNewItemOpen(false); setOpen(true); }}
        prefill={{ name: typedName }}
        onSaved={(asset) => {
          setNewItemOpen(false);
          setOpen(true);
          if (!asset?.id) { toast.error('登録した品目を取得できませんでした'); return; }
          applyItem({ id: asset.id, source: 'eng', name: asset.name, sku: asset.sku, uom: asset.uom });
          toast.success(`${asset.sku || asset.name} を品目として選びました`);
        }}
      />
    </div>
  );
}
